import type { Plugin } from "../core/plugin";
import { PluginRegistry } from "../core/pluginRegistry";

export interface CoreToggleOptions {
  trigger?: HTMLElement | null;
  target?: HTMLElement | null;
  closeOnEscape?: boolean;
  syncHost?: boolean;
}

export class CoreToggle implements Plugin {
  protected trigger: HTMLElement | null;
  protected target: HTMLElement | null;
  private closeOnEscape: boolean;
  private syncHost: boolean;

  private readonly clickHandler: (e: MouseEvent) => void;
  private readonly keydownHandler: (e: KeyboardEvent) => void;

  constructor(
    protected readonly element: HTMLElement,
    options?: CoreToggleOptions,
  ) {
    const resolved = this.resolveElements(options);
    this.trigger = resolved.trigger;
    this.target = resolved.target;
    this.closeOnEscape = options?.closeOnEscape ?? true;
    this.syncHost =
      options?.syncHost ?? this.element.dataset.toggleSyncHost !== undefined;

    this.clickHandler = (e) => this.handleClick(e);
    this.keydownHandler = (e) => this.handleKeydown(e);
  }

  init(): void {
    if (!this.trigger || !this.target) return;

    this.trigger.addEventListener("click", this.clickHandler);
    this.trigger.addEventListener("keydown", this.keydownHandler);

    const startOpen =
      this.element.dataset.toggleOpen === "true" ||
      this.trigger.getAttribute("aria-expanded") === "true";

    if (startOpen) {
      this.open();
    } else {
      this.close();
    }
  }

  destroy(): void {
    this.trigger?.removeEventListener("click", this.clickHandler);
    this.trigger?.removeEventListener("keydown", this.keydownHandler);
  }

  isOpen(): boolean {
    return this.trigger?.getAttribute("aria-expanded") === "true";
  }

  open(): void {
    if (!this.trigger || !this.target) return;
    this.trigger.setAttribute("aria-expanded", "true");
    this.target.setAttribute("aria-hidden", "false");
    if (this.syncHost && this.element !== this.trigger) {
      this.element.setAttribute("aria-expanded", "true");
    }
    this.emit(true);
  }

  close(): void {
    if (!this.trigger || !this.target) return;
    this.trigger.setAttribute("aria-expanded", "false");
    this.target.setAttribute("aria-hidden", "true");
    if (this.syncHost && this.element !== this.trigger) {
      this.element.setAttribute("aria-expanded", "false");
    }
    this.emit(false);
  }

  toggle(): void {
    if (this.isOpen()) {
      this.close();
    } else {
      this.open();
    }
  }

  setTarget(target: HTMLElement | null): void {
    this.target = target;
  }

  getTarget(): HTMLElement | null {
    return this.target;
  }

  getTrigger(): HTMLElement | null {
    return this.trigger;
  }

  private resolveElements(options?: CoreToggleOptions) {
    if (options?.trigger && options?.target) {
      return { trigger: options.trigger, target: options.target };
    }

    const el = this.element;

    const triggerSelector = el.dataset.toggleTrigger;
    const targetSelector = el.dataset.toggleTarget;

    if (triggerSelector || targetSelector) {
      const trigger =
        options?.trigger ??
        (triggerSelector ? el.querySelector<HTMLElement>(triggerSelector) : el);
      const target =
        options?.target ??
        (targetSelector
          ? (el.querySelector<HTMLElement>(targetSelector) ??
            document.querySelector<HTMLElement>(targetSelector))
          : null);
      return { trigger, target };
    }

    const controlsId = el.getAttribute("aria-controls");
    if (controlsId) {
      return {
        trigger: el,
        target: document.getElementById(controlsId),
      };
    }

    return {
      trigger: options?.trigger ?? null,
      target: options?.target ?? null,
    };
  }

  protected handleClick(e: MouseEvent): void {
    e.stopPropagation();
    this.toggle();
  }

  protected handleKeydown(e: KeyboardEvent): void {
    if (this.closeOnEscape && (e.key === "Escape" || e.key === "Esc")) {
      this.close();
      this.trigger?.focus();
      return;
    }

    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      this.toggle();
    }
  }

  private emit(open: boolean): void {
    this.element.dispatchEvent(
      new CustomEvent("coreToggle:change", {
        detail: { open },
        bubbles: true,
      }),
    );
  }
}

PluginRegistry.register("coreToggle", CoreToggle);
